import { API_BASE_URL } from '../config/api';

export interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  age?: number;
  weight?: number;
  height?: number;
  gender?: string;
  goal?: string;
  activityLevel?: string;
  dailyCalories?: number;
  dailyProtein?: number;
  dailyCarbs?: number;
  dailyFat?: number;
  notes?: string;
  profileImageUrl?: string;
  dietitianEmail?: string;
  dietitianId?: string;
  dietitianName?: string;
  isSelfRegistered?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateClientRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  age?: number;
  weight?: number;
  height?: number;
  gender?: string;
  goal?: string;
  activityLevel?: string;
  dailyCalories?: number;
  dailyProtein?: number;
  dailyCarbs?: number;
  dailyFat?: number;
  notes?: string;
  isSelfRegistered?: boolean;
  termsAcceptedAt?: string;
  termsVersion?: string;
}

export interface CreateClientResponse {
  message: string;
  client: Client;
}

export interface UpdateClientRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  age?: number;
  weight?: number;
  height?: number;
  gender?: string;
  goal?: string;
  activityLevel?: string;
  dailyCalories?: number;
  dailyProtein?: number;
  dailyCarbs?: number;
  dailyFat?: number;
  notes?: string;
  profileImageUrl?: string;
  dietitianEmail?: string;
  dietitianName?: string;
}

export interface UpdateClientResponse {
  message: string;
  client: Client;
}

export interface DeleteClientResponse {
  message: string;
}

export interface ErrorResponse {
  error: string;
}

export interface TestResponse {
  message: string;
  timestamp: string;
}

export const clientApi = {
  async getAllClients(dietitianEmail?: string): Promise<Client[]> {
    const url = dietitianEmail
      ? `${API_BASE_URL}/clients?dietitianEmail=${encodeURIComponent(
          dietitianEmail
        )}`
      : `${API_BASE_URL}/clients`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getClientById(id: string): Promise<Client> {
    const response = await fetch(`${API_BASE_URL}/clients/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getClientByEmail(email: string): Promise<Client> {
    const response = await fetch(
      `${API_BASE_URL}/clients/email/${encodeURIComponent(email)}`
    );
    if (!response.ok) {
      const error: any = new Error(`HTTP error! status: ${response.status}`);
      error.response = response;
      throw error;
    }
    return await response.json();
  },

  async createClient(
    clientData: CreateClientRequest
  ): Promise<CreateClientResponse> {
    const response = await fetch(`${API_BASE_URL}/clients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clientData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async updateClient(
    id: string,
    clientData: UpdateClientRequest
  ): Promise<UpdateClientResponse> {
    const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clientData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async deleteClient(id: string): Promise<DeleteClientResponse> {
    const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async testConnection(): Promise<TestResponse> {
    const response = await fetch(`${API_BASE_URL}/clients/test`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async selectDietitian(
    clientId: string,
    dietitianId: string
  ): Promise<UpdateClientResponse> {
    const response = await fetch(
      `${API_BASE_URL}/clients/${clientId}/select-dietitian`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dietitianId }),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async removeDietitian(clientId: string): Promise<UpdateClientResponse> {
    const response = await fetch(
      `${API_BASE_URL}/clients/${clientId}/remove-dietitian`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Diyetisyen bağlantısı kaldırılamadı');
    }
    return await response.json();
  },
};
